import type { Key9Puzzle } from '../types/puzzle';
import type { DailyPuzzleIdentity } from '../logic/puzzleIdentity';
import { getDailyProgressStorageKey } from '../logic/puzzleIdentity';
import { classicStorageKey, freshClassicProgress, migrateClassicProgress, normalizeClassicProgress, type ClassicProgress } from '../logic/classicDaily';

type StoragePort = Pick<Storage, 'getItem' | 'setItem'>;

type SaveResult = { status: 'saved'; seconds: number } | { status: 'conflict' | 'invalid' | 'unavailable' };

// Timer-only writes in another tab do not invalidate the board. Entries, notes,
// hints, completion and identity do: never merge those into an active scene.
export function createClassicSaveSession(storage: StoragePort, puzzle: Key9Puzzle, identity: DailyPuzzleIdentity) {
  const key = classicStorageKey(identity);
  const read = () => {
    const raw = storage.getItem(key);
    let progress: ClassicProgress | null = null;
    try { progress = normalizeClassicProgress(JSON.parse(raw ?? 'null'), puzzle, identity); } catch { /* Invalid records stay distinguishable from absent records. */ }
    const signature = progress
      ? JSON.stringify({ ...progress, seconds: 0 })
      : raw;
    return { progress, signature };
  };
  let expected: string | null = null;
  try { expected = read().signature; } catch { /* Keep play available when storage is blocked. */ }

  return {
    hasConflict() {
      try { return read().signature !== expected; } catch { return false; }
    },
    save(snapshot: ClassicProgress): SaveResult {
      const valid = normalizeClassicProgress(snapshot, puzzle, identity);
      if (!valid) return { status: 'invalid' };
      try {
        const current = read();
        if (current.signature !== expected) return { status: 'conflict' };
        // Completed runs are immutable even if an obsolete active snapshot arrives.
        if (current.progress?.phase === 'full_done' && JSON.stringify({ ...valid, seconds: 0 }) !== expected) return { status: 'conflict' };
        valid.seconds = current.progress?.phase === 'full_done'
          ? current.progress.seconds
          : Math.max(valid.seconds, current.progress?.seconds ?? 0);
        storage.setItem(key, JSON.stringify(valid));
        expected = JSON.stringify({ ...valid, seconds: 0 });
        return { status: 'saved', seconds: valid.seconds };
      } catch { return { status: 'unavailable' }; }
    }
  };
}

export function loadClassicProgress(storage: StoragePort, puzzle: Key9Puzzle, identity: DailyPuzzleIdentity): ClassicProgress {
  try {
    const current = normalizeClassicProgress(JSON.parse(storage.getItem(classicStorageKey(identity)) ?? 'null'), puzzle, identity);
    if (current) return current;
    const legacy = JSON.parse(storage.getItem('daysudoku:key9:v1') ?? 'null');
    const old = legacy?.days?.[getDailyProgressStorageKey(identity)] ?? legacy?.days?.[identity.dailyDate];
    return migrateClassicProgress(old, puzzle, identity) ?? freshClassicProgress(puzzle, identity);
  } catch {
    return freshClassicProgress(puzzle, identity);
  }
}

export function saveClassicProgress(storage: StoragePort, puzzle: Key9Puzzle, identity: DailyPuzzleIdentity, snapshot: ClassicProgress): boolean {
  const valid = normalizeClassicProgress(snapshot, puzzle, identity);
  if (!valid) return false;
  try {
    storage.setItem(classicStorageKey(identity), JSON.stringify(valid));
    return true;
  } catch {
    return false;
  }
}
